/**
 * Permission to Route and Menu Mappings
 * Maps permission keys to route paths, components, and menu configurations
 */

import { lazy } from 'react';
import {
  Tickets,
  CircleUserRound,
  SquarePlus,
  BookCheck,
  FileUser,
  LockKeyholeOpen,
  ChartNoAxesCombined,
  BrickWall,
  Landmark,
  ScrollText,
  Users,
  Building,
  Bell,
  Settings,
  Sparkles,
  ClipboardList,
  Layers,
  Lock,
  UserCheck,
} from 'lucide-react';
import {
  CREATE_TICKET,
  ALL_TICKETS,
  MY_TICKETS,
  MY_ASSIGNED_TICKETS,
  AWAITING_CLOSURE_TICKETS,
  CLOSED_TICKETS,
  MY_PREVIOUS_TICKETS,
  TICKET_HISTORY,
  PENDING_APPROVALS,
  VIEW_TICKET_DETAIL,
  USER_REGISTRATION,
  USER_LIST,
  USER_GROUPS,
  USER_EDIT,
  CONSULTANT_REGISTRATION,
  VIEW_ROLES,
  CREATE_ROLE,
  EDIT_ROLE,
  VIEW_PERMISSIONS,
  CREATE_PERMISSION,
  EDIT_PERMISSION,
  VIEW_OPERATIONS,
  ACCESS_CONTROL,
  COMPANY_LIST,
  CUSTOMER_DETAILS,
  VIEW_COMPANY_DETAILS,
  COMPANY_GROUPS,
  MASTER_DATA,
  FEATURES,
  BANNER_MANAGEMENT,
  APPROVAL_WORKFLOW,
  NOTIFICATIONS,
  ANALYSIS,
  SUPER_ADMIN_DASHBOARD,
  ADMIN_DASHBOARD,
  USER_DASHBOARD,
  QUEUE_MANAGER_DASHBOARD,
  APPROVER_DASHBOARD,
  CONSULTANT_DASHBOARD,
  APPROVER,
  USER_PROFILE,
  QUEUE_MANAGER_TICKETS,
  CHANGE_REQUESTS,
  CREATE_CHANGE_REQUEST,
  CHANGE_REQUEST_PENDING_APPROVALS,
  CHANGE_REQUESTS_CREATED_BY_ME,
  CHANGE_REQUESTS_ASSIGNED_TO_ME,
  MY_PREVIOUS_CHANGE_REQUESTS,
  CHANGE_REQUEST_QUEUE_MANAGER_LIST,
  CHANGE_REQUEST_MY_TASKS,
} from '../constants/permissionKeys';

// Permission to route path mapping
export const PERMISSION_TO_ROUTE = {
  [CREATE_TICKET]: 'create',
  [ALL_TICKETS]: 'all-tickets',
  [MY_TICKETS]: 'my-tickets',
  [MY_ASSIGNED_TICKETS]: 'my-assigned-tickets',
  [AWAITING_CLOSURE_TICKETS]: 'awaiting-closure',
  [CLOSED_TICKETS]: 'closed-tickets',
  [MY_PREVIOUS_TICKETS]: 'my-previous-tickets',
  [TICKET_HISTORY]: 'ticket-history',
  [PENDING_APPROVALS]: 'approvetickets',
  [VIEW_TICKET_DETAIL]: 'all-tickets/:ticketId',
  [USER_REGISTRATION]: 'user-registration',
  [USER_LIST]: 'user-list',
  [USER_GROUPS]: 'user-groups',
  [USER_EDIT]: 'user-registration/edit/:id',
  [CONSULTANT_REGISTRATION]: 'consultant-registration',
  [VIEW_ROLES]: 'roles',
  [CREATE_ROLE]: 'roles/create-role',
  [EDIT_ROLE]: 'roles/edit-role/:id/:companyId',
  [VIEW_PERMISSIONS]: 'permissions',
  [CREATE_PERMISSION]: 'permissions/create-permission',
  [EDIT_PERMISSION]: 'permissions/edit-permission/:id',
  [VIEW_OPERATIONS]: 'operations',
  [ACCESS_CONTROL]: 'access-control',
  [COMPANY_LIST]: 'company-list',
  [CUSTOMER_DETAILS]: 'company-register',
  [VIEW_COMPANY_DETAILS]: 'company-register/view/:id',
  [COMPANY_GROUPS]: 'company-groups',
  [MASTER_DATA]: 'master-data',
  [FEATURES]: 'features',
  [BANNER_MANAGEMENT]: 'baner-management',
  [APPROVAL_WORKFLOW]: 'approval-workflow',
  [NOTIFICATIONS]: 'notifications',
  [ANALYSIS]: 'analysis',
  [SUPER_ADMIN_DASHBOARD]: 'analysis',
  [ADMIN_DASHBOARD]: 'analysis',
  [USER_DASHBOARD]: 'analysis',
  [QUEUE_MANAGER_DASHBOARD]: 'analysis',
  [APPROVER_DASHBOARD]: 'analysis',
  [CONSULTANT_DASHBOARD]: 'analysis',
  [APPROVER]: 'approver',
  [USER_PROFILE]: 'myprofile',
  [QUEUE_MANAGER_TICKETS]: 'queue-manager',
  [CHANGE_REQUESTS]: 'change-requests',
  [CHANGE_REQUEST_QUEUE_MANAGER_LIST]: 'change-requests',
  [CHANGE_REQUESTS_CREATED_BY_ME]: 'change-requests/created-by-me',
  [CHANGE_REQUEST_MY_TASKS]: 'change-requests/my-tasks',
  [MY_PREVIOUS_CHANGE_REQUESTS]: 'my-previous-change-requests',
  // Use the same route; nested "create" child exists under it.
  [CREATE_CHANGE_REQUEST]: 'change-requests',
};

// Permission to component mapping
export const PERMISSION_TO_COMPONENT = {
  [CREATE_TICKET]: lazy(() => import('../components/TicketManagement/TicketForm')),
  [ALL_TICKETS]: lazy(() => import('../components/TicketManagement/AllTickets')),
  [MY_TICKETS]: lazy(() => import('../components/TicketManagement/AllTickets')),
  [MY_ASSIGNED_TICKETS]: lazy(() => import('../components/TicketManagement/AllTickets')),
  [AWAITING_CLOSURE_TICKETS]: lazy(() => import('../components/TicketManagement/AllTickets')),
  [CLOSED_TICKETS]: lazy(() => import('../components/TicketManagement/AllTickets')),
  [MY_PREVIOUS_TICKETS]: lazy(() => import('../components/TicketManagement/AllTickets')),
  [TICKET_HISTORY]: lazy(() => import('../components/TicketHistory')),
  [PENDING_APPROVALS]: lazy(() => import('../components/ApproveTicket/PendingApprovalsHub')),
  [VIEW_TICKET_DETAIL]: lazy(() => import('../components/TicketManagement/TicketDetail')),
  [USER_REGISTRATION]: lazy(() => import('../components/UserManagement/UserManagement')),
  [USER_LIST]: lazy(() => import('../components/UserManagement/UserList')),
  [USER_GROUPS]: lazy(() => import('../components/UserManagement/UserGroupsManagement')),
  [USER_EDIT]: lazy(() => import('../components/UserManagement/UserManagement')),
  [CONSULTANT_REGISTRATION]: lazy(() => import('../components/UserManagement/ConsultantRegistration')),
  [VIEW_ROLES]: lazy(() => import('../admin-service/AccessControl')),
  [CREATE_ROLE]: lazy(() => import('../admin-service/CreateRole')),
  [EDIT_ROLE]: lazy(() => import('../admin-service/CreateRole')),
  [VIEW_PERMISSIONS]: lazy(() => import('../admin-service/PermissionsList')),
  [CREATE_PERMISSION]: lazy(() => import('../admin-service/CreatePermission')),
  [EDIT_PERMISSION]: lazy(() => import('../admin-service/CreatePermission')),
  [VIEW_OPERATIONS]: lazy(() => import('../admin-service/OperationsManagement')),
  [ACCESS_CONTROL]: lazy(() => import('../admin-service/AccessControl')),
  [COMPANY_LIST]: lazy(() => import('../components/RegisterCompany/CompanyList')),
  [CUSTOMER_DETAILS]: lazy(() => import('../components/RegisterCompany/CompanyRegister')),
  [VIEW_COMPANY_DETAILS]: lazy(() => import('../components/RegisterCompany/CompanyRegister')),
  [COMPANY_GROUPS]: lazy(() => import('../components/RegisterCompany/CompanyGroupsManagement')),
  [MASTER_DATA]: lazy(() => import('../components/materData/MasterDataTables')),
  [FEATURES]: lazy(() => import('../components/materData/FeaturesManagement')),
  [BANNER_MANAGEMENT]: lazy(() => import('../components/Banner/BannerManagement')),
  [APPROVAL_WORKFLOW]: lazy(() => import('../components/settings/ApprovalWorkflowConfiguration')),
  [NOTIFICATIONS]: lazy(() => import('../components/notifications/AllNotifications')),
  [ANALYSIS]: lazy(() => import('../components/Analysis/Analysis')),
  [SUPER_ADMIN_DASHBOARD]: lazy(() => import('../components/Analysis/Analysis')),
  [ADMIN_DASHBOARD]: lazy(() => import('../components/Analysis/Analysis')),
  [USER_DASHBOARD]: lazy(() => import('../components/Analysis/Analysis')),
  [QUEUE_MANAGER_DASHBOARD]: lazy(() => import('../components/Analysis/Analysis')),
  [APPROVER_DASHBOARD]: lazy(() => import('../components/Analysis/Analysis')),
  [CONSULTANT_DASHBOARD]: lazy(() => import('../components/Analysis/Analysis')),
  [APPROVER]: lazy(() => import('../Approver-Service/Approver')),
  [USER_PROFILE]: lazy(() => import('../components/UserManagement/UserProfile')),
  [QUEUE_MANAGER_TICKETS]: lazy(() => import('../components/Analysis/QueueManagerAnalysis')),
  [CHANGE_REQUESTS]: lazy(() => import('../components/ChangeRequests/ChangeRequestList')),
  [CHANGE_REQUEST_QUEUE_MANAGER_LIST]: lazy(() =>
    import('../components/ChangeRequests/ChangeRequestList'),
  ),
  [CREATE_CHANGE_REQUEST]: lazy(() => import('../components/ChangeRequests/ChangeRequestList')),
  [CHANGE_REQUEST_PENDING_APPROVALS]: lazy(() =>
    import('../components/ApproveTicket/PendingApprovalsHub'),
  ),
  [CHANGE_REQUESTS_CREATED_BY_ME]: lazy(() =>
    import('../components/ChangeRequests/ChangeRequestCreatedByMe'),
  ),
  [CHANGE_REQUEST_MY_TASKS]: lazy(() =>
    import('../components/ChangeRequests/ConsultantMyTasksList'),
  ),
  [CHANGE_REQUESTS_ASSIGNED_TO_ME]: lazy(() =>
    import('../components/ChangeRequests/ChangeRequestMyAssigned'),
  ),
  [MY_PREVIOUS_CHANGE_REQUESTS]: lazy(() =>
    import('../components/ChangeRequests/ChangeRequestList'),
  ),
};

// Permission to menu item mapping
export const PERMISSION_TO_MENU_ITEM = {
  [CREATE_TICKET]: {
    to: 'create',
    label: 'Create New',
    icon: <SquarePlus strokeWidth={1} size={18} />,
  },
  [ALL_TICKETS]: {
    to: 'all-tickets',
    label: 'All',
    icon: <Tickets strokeWidth={1} size={18} />,
  },
  [MY_TICKETS]: {
    to: 'my-tickets',
    label: 'Created By Me',
    icon: <CircleUserRound strokeWidth={1} size={18} />,
  },
  [MY_ASSIGNED_TICKETS]: {
    to: 'my-assigned-tickets',
    label: 'Assigned To Me',
    icon: <CircleUserRound strokeWidth={1} size={18} />,
  },
  [AWAITING_CLOSURE_TICKETS]: {
    to: 'awaiting-closure',
    label: 'Awaiting Closure',
    icon: <BookCheck strokeWidth={1} size={18} />,
  },
  [CLOSED_TICKETS]: {
    to: 'closed-tickets',
    label: 'Closed Tickets',
    icon: <Lock strokeWidth={1} size={18} />,
  },
  [MY_PREVIOUS_TICKETS]: {
    to: 'my-previous-tickets',
    label: 'My Previous Tickets',
    icon: <ScrollText strokeWidth={1} size={18} />,
  },
  [PENDING_APPROVALS]: {
    to: 'approvetickets',
    label: 'Pending Approvals',
    icon: <BookCheck strokeWidth={1} size={18} />,
  },
  [USER_REGISTRATION]: {
    to: 'user-registration',
    label: 'User Registration',
    icon: <FileUser strokeWidth={1} size={18} />,
  },
  [USER_LIST]: {
    to: 'user-list',
    label: 'User List',
    icon: <Users strokeWidth={1} size={18} />,
  },
  [USER_GROUPS]: {
    to: 'user-groups',
    label: 'User Groups',
    icon: <UserCheck strokeWidth={1} size={18} />,
  },
  [ACCESS_CONTROL]: {
    to: 'access-control',
    label: 'Access Control',
    icon: <LockKeyholeOpen strokeWidth={1} size={18} />,
  },
  [COMPANY_LIST]: {
    to: 'company-list',
    label: 'Client List',
    icon: <ScrollText strokeWidth={1} size={18} />,
  },
  [CUSTOMER_DETAILS]: {
    to: 'company-register',
    label: 'Register Client',
    icon: <Landmark strokeWidth={1} size={18} />,
  },
  [COMPANY_GROUPS]: {
    to: 'company-groups',
    label: 'Client Groups',
    icon: <Layers strokeWidth={1} size={18} />,
  },
  [MASTER_DATA]: {
    to: 'master-data',
    label: 'Master Data',
    icon: <BrickWall strokeWidth={1} size={18} />,
  },
  [FEATURES]: {
    to: 'features',
    label: 'Features Management',
    icon: <Sparkles strokeWidth={1} size={18} />,
  },
  [VIEW_OPERATIONS]: {
    to: 'operations',
    label: 'Operations Management',
    icon: <Settings strokeWidth={1} size={18} />,
  },
  [BANNER_MANAGEMENT]: {
    to: 'baner-management',
    label: 'Telecast',
    icon: <ScrollText strokeWidth={1} size={18} />,
  },
  [APPROVAL_WORKFLOW]: {
    to: 'approval-workflow',
    label: 'Approval Workflow',
    icon: <Settings strokeWidth={1} size={18} />,
  },
  [NOTIFICATIONS]: {
    to: 'notifications',
    label: 'Notifications',
    icon: <Bell strokeWidth={1} size={20} />,
  },
  // Dashboard & Analytics menu is permission-driven now (not ANALYSIS).
  [SUPER_ADMIN_DASHBOARD]: {
    to: 'analysis',
    label: 'Dashboard & Analytics',
    icon: <ChartNoAxesCombined strokeWidth={1} size={20} />,
  },
  [ADMIN_DASHBOARD]: {
    to: 'analysis',
    label: 'Dashboard & Analytics',
    icon: <ChartNoAxesCombined strokeWidth={1} size={20} />,
  },
  [USER_DASHBOARD]: {
    to: 'analysis',
    label: 'Dashboard & Analytics',
    icon: <ChartNoAxesCombined strokeWidth={1} size={20} />,
  },
  [QUEUE_MANAGER_DASHBOARD]: {
    to: 'analysis',
    label: 'Dashboard & Analytics',
    icon: <ChartNoAxesCombined strokeWidth={1} size={20} />,
  },
  [CHANGE_REQUESTS]: {
    to: 'change-requests',
    label: 'All',
    icon: <ClipboardList strokeWidth={1} size={18} />,
  },
  [CHANGE_REQUEST_QUEUE_MANAGER_LIST]: {
    to: 'change-requests',
    label: 'All',
    icon: <ClipboardList strokeWidth={1} size={18} />,
  },
  [CREATE_CHANGE_REQUEST]: {
    to: 'change-requests/create',
    label: 'Create',
    icon: <SquarePlus strokeWidth={1} size={18} />,
  },
  [CHANGE_REQUEST_PENDING_APPROVALS]: {
    to: 'approvetickets?tab=change-requests',
    label: 'CR Pending Approvals',
    icon: <ClipboardList strokeWidth={1} size={18} />,
  },
  [CHANGE_REQUESTS_CREATED_BY_ME]: {
    to: 'change-requests/created-by-me',
    label: 'Created By Me',
    icon: <CircleUserRound strokeWidth={1} size={18} />,
  },
  [MY_PREVIOUS_CHANGE_REQUESTS]: {
    to: 'my-previous-change-requests',
    label: "My Previous CR's",
    icon: <ScrollText strokeWidth={1} size={18} />,
  },
  [CHANGE_REQUEST_MY_TASKS]: {
    to: 'change-requests/my-tasks',
    label: 'My Tasks',
    icon: <ClipboardList strokeWidth={1} size={18} />,
  },
};

// Menu grouping configuration
export const MENU_GROUPS = {
  'ticket-operations': {
    id: 'ticket-operations',
    label: 'Ticket Operations',
    icon: <Tickets strokeWidth={1} size={20} />,
    permissions: [CREATE_TICKET, ALL_TICKETS, MY_TICKETS, MY_PREVIOUS_TICKETS],
  },
  'change-management': {
    id: 'change-management',
    label: 'Change Management',
    icon: <ClipboardList strokeWidth={1} size={20} />,
    // Pending approvals live under My Actions (PendingApprovalsHub), not here.
    permissions: [
      CREATE_CHANGE_REQUEST,
      CHANGE_REQUESTS,
      CHANGE_REQUEST_QUEUE_MANAGER_LIST,
      CHANGE_REQUESTS_CREATED_BY_ME,
      MY_PREVIOUS_CHANGE_REQUESTS,
      CHANGE_REQUEST_MY_TASKS,
    ],
  },
  'my-actions': {
    id: 'my-actions',
    label: 'My Actions',
    icon: <CircleUserRound strokeWidth={1} size={20} />,
    permissions: [PENDING_APPROVALS, CHANGE_REQUEST_PENDING_APPROVALS, MY_ASSIGNED_TICKETS, AWAITING_CLOSURE_TICKETS, CLOSED_TICKETS],
  },
  'user-management': {
    id: 'user-management',
    label: 'User Management',
    icon: <Users strokeWidth={1} size={20} />,
    permissions: [USER_REGISTRATION, USER_LIST, USER_GROUPS, ACCESS_CONTROL],
  },
  'company-management': {
    id: 'company-management',
    label: 'Client Management',
    icon: <Building strokeWidth={1} size={20} />,
    permissions: [CUSTOMER_DETAILS, COMPANY_LIST, COMPANY_GROUPS],
  },
  'system-configuration': {
    id: 'system-configuration',
    label: 'System Configuration',
    icon: <Settings strokeWidth={1} size={20} />,
    // Keep Operations/Features at the bottom of this dropdown
    permissions: [MASTER_DATA, BANNER_MANAGEMENT, APPROVAL_WORKFLOW, VIEW_OPERATIONS, FEATURES],
  },
};


